<template>
  <div class="message-board-container">
    <h2>留言板</h2>
    <!-- 添加温馨提示 -->
    <div class="message-tip">
      <el-alert
        title="温馨提示"
        type="info"
        description="欢迎在留言板提出您关于体测的任何问题，老师会尽快回复您！请放心提问，我们将竭诚为您服务。"
        show-icon
        :closable="false"
      ></el-alert>
    </div>
    
    <!-- 学生留言列表 -->
    <div class="message-list">
      <div v-if="studentMessages.length === 0" class="empty-message">
        <el-empty description="暂无留言记录" />
      </div>
      
      <div v-for="message in studentMessages" :key="message.id" class="message-item">
        <div class="message-header">
          <span class="author">我</span>
          <div class="message-meta">
            <el-tag :type="message.status === '已回复' ? 'success' : 'info'" size="small" class="status-tag">
              {{ message.status }}
            </el-tag>
            <span class="time">{{ message.time }}</span>
          </div>
        </div>
        <div class="message-content">
          {{ message.content }}
        </div>
        
        <!-- 老师回复 -->
        <div v-if="message.teacherReply" class="teacher-reply">
          <div class="reply-header">
            <span class="reply-author">老师回复</span>
            <span class="reply-time">{{ message.replyTime }}</span>
          </div>
          <div class="reply-content">
            {{ message.teacherReply }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- 留言输入区 -->
    <div class="message-input">
      <el-input
        type="textarea"
        :rows="4"
        placeholder="请输入留言内容..."
        v-model="messageContent"
        maxlength="500"
        show-word-limit
      ></el-input>
      <el-button type="primary" class="send-btn" @click="sendMessage">发送留言</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage, ElEmpty, ElAlert, ElTag } from 'element-plus';
import request from '@/utils/request';

const messageContent = ref('');
const studentMessages = ref([]);

// 获取学生留言列表
const fetchStudentMessages = async () => {
  try {
    // 调用API获取学生留言列表（包含老师回复）
    const response = await request.get('/student/consultation/student/list');
    if (response.code === 200 && response.data) {
      // 将后端返回的Consultation对象映射为前端组件需要的格式
      const formattedMessages = response.data.map(item => ({
        id: item.consultId,
        content: item.questionContent,
        teacherReply: item.answerContent,
        status: item.status || '未回复',
        time: item.createTime ? new Date(item.createTime).toLocaleString() : '',
        replyTime: item.answerContent ? new Date(item.createTime).toLocaleString() : ''
      }));
      
      // 按照时间倒序排列，最新的留言在前面
      studentMessages.value = formattedMessages.sort((a, b) => 
        new Date(b.time) - new Date(a.time)
      );
    }
  } catch (error) {
    console.error('获取留言列表失败:', error);
    // 不加载模拟数据，保持列表为空
  }
};

// 发送留言
const sendMessage = async () => {
  if (!messageContent.value.trim()) {
    ElMessage.warning('请输入留言内容');
    return;
  }
  try {
    // 调用在线咨询API提交留言
    const response = await request.post('/student/consultation/submit', {
      questionContent: messageContent.value,
      type: 1 // 使用整数类型，1表示留言板类型
    });
    
    if (response.code === 200) {
      ElMessage.success('留言发送成功');
      // 清空输入框
      messageContent.value = '';
      // 重新获取留言列表
      fetchStudentMessages();
    } else {
      ElMessage.error(response.message || '留言发送失败');
    }
  } catch (error) {
    console.error('发送留言失败:', error);
    ElMessage.error('留言发送失败，请稍后重试');
  }
};

// 组件挂载时获取留言列表
onMounted(() => {
  fetchStudentMessages();
});
</script>

<style scoped>
.message-board-container {
  padding: 20px;
}

.message-tip {
  margin-bottom: 20px;
}

.message-list {
  margin-bottom: 30px;
}

.message-item {
  padding: 15px;
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  position: relative;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-tag {
  margin-right: 10px;
}

.author {
  font-weight: 500;
  color: #1890ff;
}

.time,
.reply-time {
  color: #999;
  font-size: 12px;
}

.message-content {
  color: #333;
  line-height: 1.6;
  margin-bottom: 10px;
}

/* 老师回复样式 */
.teacher-reply {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #e0e0e0;
}

.reply-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.reply-author {
  font-weight: 500;
  color: #52c41a;
}

.reply-content {
  color: #666;
  line-height: 1.6;
  padding: 10px;
  background-color: #f6ffed;
  border-radius: 4px;
  border-left: 3px solid #52c41a;
}

.empty-message {
  text-align: center;
  padding: 40px 0;
}

.message-input {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.send-btn {
  margin-top: 15px;
  float: right;
}

/* 清除浮动 */
.message-input::after {
  content: '';
  display: table;
  clear: both;
}
</style>